import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  TextField, IconButton, Chip, Alert, Divider, Tooltip,
  CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import {
  isChartable, isScanEligible, describeUnsupported, describeScanCadence, scanTickCadence,
  intervalLabel, cadenceFor, BUILTIN_INTERVALS, parseIntervalLabel,
} from '../../utils/intervalCatalog';
import {
  customIntervalService, type IntervalVocabulary, type IntervalReferences,
} from '../../services/customInterval.service';

/** The TICK cadence in words. "every 20 minutes", "every 4 hours", "once a day".
 *
 *  For an interval that divides the day this is the bar size itself. For one
 *  that does not (7H), no cadence can equal it — `cadenceFor(420)` returns
 *  {MINUTE, 420}, which the schedule validator rejects outright because MINUTE
 *  is capped at 59 — so the scan ticks on the finest grid that contains every
 *  close instead, and emits once per closed bar. Computed for a 24x7 venue,
 *  which is where non-divisor intervals are actually used. */
function cadenceSentence(minutes: number): string {
  const c = isScanEligible(minutes)
    ? cadenceFor(minutes)
    : scanTickCadence(minutes, { venue: 'DELTA', sessionOffsetSec: 0 });
  const unit = c.scheduleType === 'MINUTE' ? 'minute' : c.scheduleType === 'HOUR' ? 'hour' : 'day';
  if (c.scheduleValue === 1) return unit === 'day' ? 'once a day' : `every ${unit}`;
  return `every ${c.scheduleValue} ${unit}s`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fires whenever the vocabulary changes so the picker can re-read it. */
  onChanged?: (vocab: IntervalVocabulary) => void;
}

/**
 * Create and manage user-defined candle intervals.
 *
 * The two rules this dialog exists to make legible:
 *   1. Anything the catalog calls chartable can be charted. A value that does
 *      not divide 1440 (7m, 23m) is fine on a chart — its bucket grid restarts
 *      at each session open and the last bucket of the day is short, exactly as
 *      TradingView does it.
 *   2. Only a divisor of 1440 (plus 1D / 1W / 1M) can drive a SCAN, because the
 *      scheduler's fire grid provably coincides with the candle grid only for
 *      those. The switch is disabled for the rest, with the reason on screen —
 *      never silently ignored.
 */
const CustomIntervalsDialog: React.FC<Props> = ({ open, onClose, onChanged }) => {
  const [vocab, setVocab] = useState<IntervalVocabulary | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState('');
  const [draftLabel, setDraftLabel] = useState('');

  // Delete confirmation: what still points at the interval being removed.
  const [pendingDelete, setPendingDelete] = useState<{ minutes: number; refs: IntervalReferences } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const v = await customIntervalService.list();
      setVocab(v);
      onChanged?.(v);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Could not load intervals.');
    } finally {
      setLoading(false);
    }
  }, [onChanged]);

  useEffect(() => { if (open) void reload(); }, [open, reload]);

  // Accept a bare number ("20") or one of the app's own labels ("13H", "2D").
  const draftMinutes = useMemo(() => {
    const t = draft.trim();
    if (!t) return null;
    return /^\d+$/.test(t) ? parseInt(t, 10) : parseIntervalLabel(t);
  }, [draft]);

  const draftProblem = useMemo(() => {
    if (draftMinutes == null) return draft.trim() ? 'Enter a number of minutes, or a label like 13H or 2D.' : null;
    const why = describeUnsupported(draftMinutes);
    if (why) return why;
    if (BUILTIN_INTERVALS.includes(draftMinutes)) return `${intervalLabel(draftMinutes)} is already available.`;
    if (vocab?.custom.some(c => c.minutes === draftMinutes)) return `${intervalLabel(draftMinutes)} is already in your list.`;
    return null;
  }, [draftMinutes, draft, vocab]);

  // Anything CHARTABLE can drive a scan now — no opt-in, no gate. `coincident`
  // only decides HOW the sentence below is worded: whether a scheduler cadence
  // can equal the bar size, or whether the scan ticks finer and emits per closed
  // bar. It is not a permission.
  const draftCoincident = draftMinutes != null && isScanEligible(draftMinutes);

  const add = async () => {
    if (draftMinutes == null || draftProblem) return;
    setBusy(true);
    try {
      // No `enabledForScan` — defining an interval IS the opt-in now, and the
      // server treats an absent field as "leave it alone" (new rows default on).
      await customIntervalService.upsert({
        minutes: draftMinutes,
        label: draftLabel.trim() || undefined,
      });
      setDraft(''); setDraftLabel('');
      await reload();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Could not add that interval.');
    } finally { setBusy(false); }
  };

  const askDelete = async (minutes: number) => {
    setBusy(true);
    try {
      setPendingDelete({ minutes, refs: await customIntervalService.references(minutes) });
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Could not check what uses this interval.');
    } finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await customIntervalService.remove(pendingDelete.minutes);
      setPendingDelete(null);
      await reload();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Could not remove that interval.');
    } finally { setBusy(false); }
  };

  const custom = vocab?.custom || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>Custom intervals</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Any whole number of minutes can be charted, and anything you add here is immediately
          available to Strategies — for ad-hoc scans, schedules and auto-trade. No second opt-in.
          A value that divides 1440 gets a cadence equal to the bar; one that does not (7H) is
          scanned by ticking on a finer grid and emitting once per CLOSED bar, so the scan never
          fires mid-bar — it just looks more often than it finds.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* ── add ─────────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            size="small" label="Minutes" value={draft} sx={{ width: 130 }}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !draftProblem) void add(); }}
            placeholder="20"
            error={!!draftProblem}
          />
          <TextField
            size="small" label="Name (optional)" value={draftLabel} sx={{ width: 160 }}
            onChange={e => setDraftLabel(e.target.value)}
            placeholder={draftMinutes != null && isChartable(draftMinutes) ? intervalLabel(draftMinutes) : '20m'}
            inputProps={{ maxLength: 16 }}
          />
          <Button size="small" variant="contained" startIcon={<AddIcon />} sx={{ mt: 0.4 }}
            disabled={busy || draftMinutes == null || !!draftProblem} onClick={() => void add()}>
            Add
          </Button>
        </Box>

        {draftProblem && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>{draftProblem}</Typography>
        )}
        {!draftProblem && draftMinutes != null && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {intervalLabel(draftMinutes)} — {draftCoincident
              ? `can be charted and scanned. A scan on it would run ${cadenceSentence(draftMinutes)}.`
              : describeScanCadence(draftMinutes, { venue: 'DELTA', sessionOffsetSec: 0 })}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ── existing ────────────────────────────────────────────────── */}
        {loading && !vocab ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : custom.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No custom intervals yet. The builtin ladder ({BUILTIN_INTERVALS.map(intervalLabel).join(' · ')}) is always available.
          </Typography>
        ) : (
          custom.map(c => (
            <Box key={c.minutes} sx={{
              display: 'flex', alignItems: 'center', gap: 1, py: 0.6,
              borderBottom: theme => `1px solid ${theme.palette.divider}`,
            }}>
              <Chip size="small" label={c.label || intervalLabel(c.minutes)} sx={{ fontWeight: 700, minWidth: 62 }} />
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 74 }}>{c.minutes} min</Typography>
              <Tooltip arrow title={c.cadenceNote || `A scheduled scan on this interval ticks ${cadenceSentence(c.minutes)}`}>
                <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                  scans tick {cadenceSentence(c.minutes)}
                </Typography>
              </Tooltip>
              <Box sx={{ flex: 1 }} />
              <Tooltip arrow title="Remove from the timeframe picker">
                <span><IconButton size="small" disabled={busy} onClick={() => void askDelete(c.minutes)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton></span>
              </Tooltip>
            </Box>
          ))
        )}

        {/* ── delete confirmation, with the references spelled out ────── */}
        {pendingDelete && (
          <Alert
            severity={pendingDelete.refs.blockedByLiveAutoTrade ? 'error' : 'warning'}
            sx={{ mt: 2 }}
            action={
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button size="small" onClick={() => setPendingDelete(null)}>Cancel</Button>
                <Button size="small" color="error" disabled={busy || pendingDelete.refs.blockedByLiveAutoTrade}
                  onClick={() => void confirmDelete()}>Remove</Button>
              </Box>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Remove {intervalLabel(pendingDelete.minutes)}?
            </Typography>
            {pendingDelete.refs.blockedByLiveAutoTrade ? (
              <Typography variant="caption" sx={{ display: 'block' }}>
                {pendingDelete.refs.scheduledScans.filter(s => s.enabled && s.autoTrade).length} enabled auto-trading
                scan(s) run on it. Turn auto-trade or the schedule off first — removing it here would hide a scan that
                is still placing orders.
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ display: 'block' }}>
                {pendingDelete.refs.scheduledScans.length === 0 && pendingDelete.refs.htfMappings.length === 0
                  ? 'Nothing is using it.'
                  : `${pendingDelete.refs.scheduledScans.length} scan(s) and ${pendingDelete.refs.htfMappings.length} timeframe-mapping row(s) refer to it. They keep working — this only takes it out of the picker.`}
              </Typography>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomIntervalsDialog;
